import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { selectActiveCompany } from "../../../../store/activeCompanySlice";
import { ClaimFirstEmptyState } from "../ClaimFirstEmptyState";
import {
  useRequestVerificationMutation,
  useGetCompanyRequestStatusMutation,
} from "../../../../store/Company/companyApiSlice";
import { CommonToast } from "../../../common/Toast";
import { MEMBERSHIP_PLANS } from "../../Dashboard/membership/membershipContent";
import { MembershipHeader } from "./MembershipHeader";
import { MembershipStatusRow } from "./MembershipStatusRow";
import { PlanSelector } from "./PlanSelector";
import { MembershipBenefits } from "./MembershipBenefits";
import { MembershipPanel } from "./MembershipPanel";
import { VerificationPanel } from "./VerificationPanel";
import { StatusGlyph, Dirham, GemIcon } from "../../../../assets/ui/icons";

/* u1 Membership — TIP-633.
 *
 * Visual uplift only. Everything about *what* this screen does is transcribed
 * from components/admin/Dashboard/membership/MembershipStatusCard.jsx: the same
 * active-company selector, the same profile_status ladder deciding which state
 * shows, the same plan defaulting to yearly, and the same handoff to the
 * checkout screen carrying the chosen cycle in router state. No payment happens
 * here and none was added — this screen picks a cycle and navigates.
 */

/* The plan cards, built from the prices the current screen already uses so the
   two cannot quote different numbers. */
export const planOptions = (plans = MEMBERSHIP_PLANS) => [
  {
    id: plans.yearly.id,
    label: plans.yearly.label,
    price: plans.yearly.price,
    unit: "/ year",
    /* A node, not a string, so the monthly equivalent carries the Dirham glyph
       the price above it uses rather than the letters "AED". */
    note: (
      <>
        Just <Dirham />
        {plans.yearly.perMonth} / month, billed annually
      </>
    ),
    footnote: "Save ~16% vs monthly",
    ribbon: "Best value",
  },
  {
    id: plans.monthly.id,
    label: plans.monthly.label,
    price: plans.monthly.price,
    unit: "/ month",
    note: "Billed monthly · most flexible",
    footnote: "No long-term commitment",
    footnoteTone: "muted",
  },
];

/* The membership mark for the empty state's circle: GemIcon in coral — the SAME glyph and colour as the one beside the page title, rather than the flat inline... */
const MembershipDiamond = () => (
  <span className="flex text-[var(--coral)]">
    <GemIcon size={30} />
  </span>
);

export const MembershipCard = () => {
  const active = useSelector(selectActiveCompany);
  const { token, userInfo } = useSelector((state) => state.auth);
  const individualID = userInfo?.individual?.[0]?.individualID;
  const navigate = useNavigate();
  const [requestVerification, { isLoading: isRequesting }] = useRequestVerificationMutation();
  const [getRequestStatus] = useGetCompanyRequestStatusMutation();
  const [plan, setPlan] = useState("yearly");
  const [verifyStatus, setVerifyStatus] = useState("idle");

  /* Transcribed verbatim. A verification request already in flight has to survive a reload — "in review" is a fact about the account, not a local UI flag — so... */
  useEffect(() => {
    const companyID = active?.companyID;
    if (!companyID || active?.profile_status !== "member" || !individualID || !token) return;
    let cancelled = false;
    getRequestStatus({ companyID, individualID, token })
      .unwrap()
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || res?.results || [];
        const pending = list.some((r) => String(r?.status || "").toLowerCase() === "pending");
        if (!cancelled && pending) setVerifyStatus("review");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [active?.companyID, active?.profile_status, individualID, token, getRequestStatus]);

  /* Transcribed guard: without a company in the switcher there is nothing to
     show a plan for. */
  if (!active?.companyID) {
    return (
      <div className="u1-root">
        <MembershipHeader />
        {/* Membership belongs to a business, so an account with none cannot
            buy one yet. The design sends them to claim first rather than
            leaving them at a switcher with nothing in it. */}
        <ClaimFirstEmptyState
          className="mt-6"
          icon={<MembershipDiamond />}
          title="Claim your business before you become a Member."
        >
          Membership belongs to a business, not a personal account. Find and
          claim yours for free, then come back here to join and get the Member
          rate, the Partner Program and Payment Endorsements.
        </ClaimFirstEmptyState>
      </div>
    );
  }

  const { companyID, name, profile_status: status } = active;
  const isMember = status === "member";
  const isVerified = status === "verified";
  const isClaimed = !isMember && !isVerified;

  /* Transcribed verbatim, scroll reset included — the checkout screen is long
     and arriving mid-page reads as a broken link. */
  const goToCheckout = (chosen) => {
    window.scrollTo(0, 0);
    navigate(`/membership/${companyID}`, { state: { plan: chosen || plan } });
  };

  /* Transcribed verbatim, including which side of the request the toast fires
     on: the panel only moves to "review" once the server has accepted it. */
  const handleRequest = async () => {
    try {
      await requestVerification({ companyID, token }).unwrap();
      setVerifyStatus("review");
      CommonToast.success("Verification requested — our team will review it shortly.");
    } catch (err) {
      CommonToast.error(err?.data?.detail || "Couldn't request verification. Please try again.");
    }
  };

  return (
    <div className="u1-root max-w-[1080px] pt-6">
      <MembershipHeader />

      <MembershipStatusRow
        name={name}
        status={isVerified ? "verified" : "claimed"}
        member={isMember}
        hint={
          isClaimed && (
            <>
              <span className="flex shrink-0 text-[var(--gold-star)]">
                <StatusGlyph kind="claimed" size={16} />
              </span>
              Become a member to get verified
            </>
          )
        }
      />

      {isClaimed && (
        <>
          <PlanSelector
            plans={planOptions()}
            value={plan}
            onChange={setPlan}
            onSubmit={() => goToCheckout(plan)}
          />
          <MembershipBenefits />
        </>
      )}

      {/* Member and verified share one layout — what a member is buying sits beside what verification would add, and a verified company sees the same pair with the... */}
      {/* The two panels are one pair, so they share a height: the grid's own `stretch` does it, and `items-start` was what let the shorter one stop early and leave... */}
      {!isClaimed && (
        <div className="mt-2 grid grid-cols-1 gap-[22px] md:grid-cols-2">
          <MembershipPanel
            companyName={active?.name}
            /* Not serialised yet — CompanyMembership.current_period_end is in
               the database but no endpoint returns it, so the renewal line
               stays hidden until one does rather than showing a made-up date. */
            renewalDate={active?.membership?.current_period_end}
          />
          <VerificationPanel
            state={isVerified ? "verified" : verifyStatus}
            isRequesting={isRequesting}
            onRequest={handleRequest}
            onCancel={() => setVerifyStatus("idle")}
            onViewProfile={() => navigate(`/search/${companyID}`)}
          />
        </div>
      )}
    </div>
  );
};

export default MembershipCard;
